import { useState } from 'react';
import { Switch, Route, Redirect, useHistory } from 'react-router-dom';

import { PlanEnum } from 'api-bible-shared-lib/utils/types';

import { Alert, AlertType } from '../../components/Alert';
import Card from '../../components/Card';
import Container from '../../components/Container';
import Step from '../../components/Step';
import { IconDownChevron } from '../../components/Icons';
import ApplicationForm from '../../components/ApplicationForm';
import PlanSelection from '../../components/PlanSelection';
import { LoadingPage } from '../../components/Loading';

import { useAccountUser } from '../../contexts/User';
import { useApplications } from '../../contexts/Applications';

import { AccountForm, AccountFormState } from '../../components/AccountForm';

const step0Url = '/registration';
const step1Url = '/registration/profile';
const step2Url = '/registration/application/plan';
const step3Url = '/registration/application/details';

export default function Registration() {
  const history = useHistory();
  const { registrationCompleted, updateAccount, updateUser } = useAccountUser();

  const { applicationsById, defaultApplicationId, createApplication, loaded } = useApplications();
  const app = applicationsById[defaultApplicationId];
  const alreadyHasApplication = Object.values(app || {}).some((value) => Boolean(value));

  const [error, setError] = useState<Error | undefined>(undefined);
  const [success, setSuccess] = useState<string | undefined>(undefined);

  if (!loaded) return <LoadingPage />;

  const handleAccountFormSubmit = async (values: AccountFormState) => {
    setError(undefined);
    setSuccess(undefined);
    try {
      await Promise.all([updateAccount(values.account), updateUser(values.user)]);
      history.push(step2Url);
    } catch (e) {
      const err = e as Error;
      setError(err);
    }
  };

  return (
    <Container>
      <Switch>
        {/* Redirect to first step or dashboard based on current use and application state */}
        <Route exact path={step0Url}>
          {registrationCompleted && alreadyHasApplication ? (
            <Redirect to="/application" />
          ) : (
            <Redirect to={step1Url} />
          )}
        </Route>

        {/* step 1 */}
        <Route exact path={step1Url}>
          <Step
            stepNumber={1}
            title="How do you intend to use this API?"
            subtitle="Create Acccount"
          >
            <Card className="px-12 py-12">
              {success && (
                <Alert
                  className="mb-8"
                  type={AlertType.SUCCESS}
                  title="Success!"
                  message={success}
                />
              )}
              {error && (
                <Alert
                  className="mb-8"
                  type={AlertType.ERROR}
                  title="There was an error with your submission"
                  message={error?.message || 'An unexpected error occurred.'}
                />
              )}

              <AccountForm handleSubmit={handleAccountFormSubmit} isRegistration />
            </Card>
          </Step>
        </Route>

        {/* step 2 */}
        <Route path={step2Url}>
          <Step
            stepNumber={2}
            title="Choose your plan."
            className="max-w-5xl"
            subtitle="Create Application"
          >
            <Card className="px-12 py-12">
              <PlanSelection
                handlePlanSelect={(plan) => history.push(`${step3Url}?plan=${plan}`)}
              />
            </Card>
          </Step>
        </Route>

        {/* step 3 */}
        <Route
          exact
          path={step3Url}
          render={({ location }) => {
            const plan = new URLSearchParams(location.search).get('plan') || '';
            if (!Object.values(PlanEnum).includes(plan as PlanEnum))
              return <Redirect to={step2Url} />;

            return (
              <Step stepNumber={3} title="Create your application." subtitle="New Application">
                <Card className="px-12 py-12">
                  <ApplicationForm
                    backUrl={step2Url}
                    initialAppValues={{ plan: plan as PlanEnum }}
                    handleSubmit={(appInfo) =>
                      createApplication(appInfo).then((createdApp) => {
                        history.push(`/application/${createdApp.id}`);
                      })
                    }
                    submitText={
                      <>
                        Create App <IconDownChevron className="w-6 h-6 transform -rotate-90" />
                      </>
                    }
                  />
                </Card>
              </Step>
            );
          }}
        />
      </Switch>
    </Container>
  );
}
